// Page library editor: create/rename/edit archetype pages for a product type.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  deleteProposalPage,
  listProposalPages,
  upsertProposalPage,
} from "@/lib/opportunity/proposalPageClient";
import { ensurePrimaryProposalPackage } from "@/lib/opportunity/proposalPrimaryPackage";
import {
  PRIMARY_PAGE_KEYS,
  PROPOSAL_PAGE_ARCHETYPE_LABELS,
  PROPOSAL_PAGE_ARCHETYPES,
  defaultBodyForArchetype,
  type ProposalPage,
  type ProposalPageArchetype,
  type ProposalPageBody,
} from "@/lib/opportunity/proposalPageTypes";
import { SectionTabSlider } from "./SectionTabSlider";
import { ProposalPageBodyEditor } from "./ProposalPageBodyEditor";

type Props = {
  formatKey: OpportunityFormatKey;
  productType: string;
  onChanged?: () => void;
  onPreviewPage?: (pageId: string) => void;
};

export function ProposalPagesPanel({
  formatKey,
  productType,
  onChanged,
  onPreviewPage,
}: Props) {
  const [archetype, setArchetype] = useState<ProposalPageArchetype>("cover_letter");
  const [pages, setPages] = useState<ProposalPage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listProposalPages({
      format_key: formatKey,
      product_type: productType,
      ensure: true,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setPages([]);
      return;
    }
    setSupported(r.data.pages_supported !== false);
    if (r.data.pages_supported === false) {
      setError(r.data.error ?? "Page library migration required.");
      setPages([]);
      return;
    }
    setPages(r.data.pages);
  }, [formatKey, productType]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(
    () => pages.filter((p) => p.archetype === archetype),
    [pages, archetype],
  );

  useEffect(() => {
    if (filtered.some((p) => p.id === selectedId)) return;
    setSelectedId(filtered[0]?.id ?? null);
  }, [filtered, selectedId]);

  const selected = filtered.find((p) => p.id === selectedId) ?? null;

  async function createAlternatePage() {
    const base = filtered.find((p) => p.page_key === PRIMARY_PAGE_KEYS[archetype]) ?? filtered[0];
    const slug = `${archetype}_${Date.now().toString(36)}`.slice(0, 48);
    const body = base?.body ?? defaultBodyForArchetype(formatKey, archetype);
    const r = await upsertProposalPage({
      format_key: formatKey,
      product_type: productType,
      page_key: slug,
      label: `${PROPOSAL_PAGE_ARCHETYPE_LABELS[archetype]} alternate`,
      archetype,
      body,
      sort_order: (filtered[filtered.length - 1]?.sort_order ?? 0) + 10,
    });
    if (!r.ok) {
      setError(r.error);
      return;
    }
    const ensured = await ensurePrimaryProposalPackage({ formatKey, productType });
    if (!ensured.ok) {
      setError(ensured.error);
      return;
    }
    await reload();
    setSelectedId(r.data.page.id);
    onChanged?.();
  }

  async function removePage(page: ProposalPage) {
    if (page.page_key === PRIMARY_PAGE_KEYS[page.archetype]) return;
    if (!window.confirm(`Delete page “${page.label}”?`)) return;
    const r = await deleteProposalPage({
      format_key: formatKey,
      product_type: productType,
      page_key: page.page_key,
    });
    if (!r.ok) {
      setError(r.error);
      return;
    }
    await reload();
    onChanged?.();
  }

  const archetypeTabs = PROPOSAL_PAGE_ARCHETYPES.map((id) => ({
    id,
    label: PROPOSAL_PAGE_ARCHETYPE_LABELS[id],
  }));

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 dark:text-zinc-400">
        Author page copy in the library, then assemble packages from these pages. Primary pages feed
        the Primary package (created automatically on first save).
      </p>

      {!supported || error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          {error ?? "Page library unavailable."}
          {!supported ? (
            <span className="mt-1 block font-mono text-[10px]">
              docs/sql/opp_proposal_pages_v1.sql
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="flex justify-center">
        <SectionTabSlider
          options={archetypeTabs}
          value={archetype}
          onChange={setArchetype}
          ariaLabel="Page archetype"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {filtered.map((p) => {
          const isPrimary = p.page_key === PRIMARY_PAGE_KEYS[p.archetype];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedId(p.id)}
              className={
                "rounded-lg border px-2.5 py-1 text-xs " +
                (selectedId === p.id
                  ? "border-indigo-500 bg-indigo-50 text-indigo-800 dark:border-indigo-400 dark:bg-indigo-950/40 dark:text-indigo-200"
                  : "border-gray-200 bg-white text-gray-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200")
              }
            >
              {p.label}
              {isPrimary ? (
                <span className="ml-1 text-[10px] uppercase text-gray-400">primary</span>
              ) : null}
            </button>
          );
        })}
        <button
          type="button"
          disabled={loading || !supported}
          onClick={() => void createAlternatePage()}
          className="rounded-lg border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-600 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-300"
        >
          + New {PROPOSAL_PAGE_ARCHETYPE_LABELS[archetype].toLowerCase()}
        </button>
      </div>

      {selected ? (
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="font-mono text-[10px] text-gray-400">{selected.page_key}</p>
            {selected.page_key !== PRIMARY_PAGE_KEYS[selected.archetype] ? (
              <button
                type="button"
                onClick={() => void removePage(selected)}
                className="rounded border border-red-300 px-2 py-0.5 text-[11px] text-red-700 dark:border-red-800 dark:text-red-300"
              >
                Delete
              </button>
            ) : null}
          </div>
          <ProposalPageBodyEditor
            page={selected}
            onPreview={onPreviewPage}
            onSave={async (body: ProposalPageBody, label: string) => {
              const r = await upsertProposalPage({
                format_key: formatKey,
                product_type: productType,
                page_key: selected.page_key,
                label,
                archetype: selected.archetype,
                body,
                sort_order: selected.sort_order,
              });
              if (r.ok) {
                const ensured = await ensurePrimaryProposalPackage({ formatKey, productType });
                if (!ensured.ok) {
                  return { ok: false, error: ensured.error };
                }
                await reload();
                onChanged?.();
              }
              return r.ok ? { ok: true } : { ok: false, error: r.error };
            }}
          />
        </div>
      ) : loading ? (
        <p className="text-xs text-gray-500">Loading pages…</p>
      ) : (
        <p className="text-xs text-gray-500">No pages for this archetype yet.</p>
      )}
    </div>
  );
}
